import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PIP_AND_THE_PAPER_MOON } from '@/content/stories';
import { LIVING_SCENES, TEMPLATES } from '@/content/templates';
import { Scene } from '@/content/scenes';
import { StoryReader } from '@/features/reader/StoryReader';
import { StoryActivities, type ParticipationMethod } from '@/features/activities/StoryActivities';
import { ColoringStudio } from '@/features/studio/ColoringStudio';
import { useStudio } from '@/features/studio/useStudio';
import { LivingScene } from '@/features/editions/LivingScene';
import { ArtworkService } from '@/services/artwork';
import { LocalStore } from '@/platform/localStore';
import { Button } from '@/design-system/Button';
import { Card, Chip, SectionHeading } from '@/design-system/Surfaces';
import { SparkleBurst, SparkleField, SparkleGlyph } from '@/design-system/Sparkle';
import { Logo } from '@/design-system/Logo';
import { cn } from '@/design-system/cn';
import { IconArrow, IconBack } from '@/features/studio/Icons';
import { useWide } from '@/app/useWide';
import type { Artwork } from '@/contracts/family';
import type { ReadingMode } from '@/contracts/content';

type Stage = 'intro' | 'read' | 'explore' | 'choose' | 'draw' | 'result';

/** The sample account namespace. Kept separate from any real family data. */
const GUEST = 'guest';

/**
 * The complete public sample (spec §5 W08).
 *
 * Runs entirely on the device: one full story, a participation activity, a
 * coloring choice, a real saved result and a living-scene demonstration.
 * It asks for no child name, no account and no card, uploads nothing, and
 * exposes no public posting or reactions. Creations are stored locally so
 * they can be offered for import after a parent sets up an account.
 */
export function TrySample() {
  const navigate = useNavigate();
  const story = PIP_AND_THE_PAPER_MOON;
  const wide = useWide();

  const [stage, setStage] = useState<Stage>('intro');
  const [pageId, setPageId] = useState<string | null>(story.currentVersion.pages[0].id);
  const [mode, setMode] = useState<ReadingMode>('listen-together');
  const [participation, setParticipation] = useState<ParticipationMethod | null>(null);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [artwork, setArtwork] = useState<Artwork | null>(null);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [livingUrl, setLivingUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showLiving, setShowLiving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const template = useMemo(
    () => story.currentVersion.templates.find((t) => t.id === templateId) ?? null,
    [story, templateId],
  );

  const { engine, saveState, requestSave, flush, restoreProblem } = useStudio(GUEST, artwork, template);

  // Resume where the visitor left off, so a refresh mid-sample is not a reset.
  useEffect(() => {
    (async () => {
      const saved = await LocalStore.get<{ stage: Stage; pageId: string | null; templateId: string | null; artworkId: string | null }>(
        'meta', GUEST, 'sample-progress',
      );
      if (!saved) return;
      if (saved.pageId) setPageId(saved.pageId);
      if (saved.templateId) setTemplateId(saved.templateId);
      if (saved.artworkId) {
        const a = await ArtworkService.get(GUEST, saved.artworkId);
        if (a) setArtwork(a);
      }
    })();
  }, []);

  useEffect(() => {
    void LocalStore.put('meta', GUEST, 'sample-progress', {
      stage,
      pageId,
      templateId,
      artworkId: artwork?.id ?? null,
    });
  }, [stage, pageId, templateId, artwork]);

  const openTemplate = useCallback(
    async (id: string) => {
      const tpl = story.currentVersion.templates.find((t) => t.id === id);
      if (!tpl) return;
      setTemplateId(id);
      const a = await ArtworkService.openForTemplate(
        GUEST, 'sample-child', story.id, tpl.id, tpl.revision, tpl.title,
      );
      setArtwork(a);
      setStage('draw');
    },
    [story],
  );

  const finish = useCallback(async () => {
    if (!engine || !artwork || !template) return;
    setBusy(true);
    setSaveError(null);
    try {
      // Local write first — the result screen is only shown once the picture
      // is genuinely stored (spec §9.3 steps 1-5).
      await flush();
      const preview = await engine.toBlob(1);
      await ArtworkService.finish(GUEST, artwork, engine.snapshot(), preview, `sample-${artwork.id}`);
      setResultUrl(engine.toDataUrl(1, null));
      const scene = template.livingSceneId ? LIVING_SCENES[template.livingSceneId] : null;
      setLivingUrl(scene ? engine.toDataUrl(1, null, scene.omitRegions) : null);
      setStage('result');
    } catch {
      setSaveError(
        'Your picture is still on screen, but this device would not save it. Try Done again, or free up some space.',
      );
    } finally {
      setBusy(false);
    }
  }, [engine, artwork, template, flush]);

  /* ------------------------------- Stages ------------------------------- */

  if (stage === 'read') {
    return (
      <StoryReader
        story={story}
        pageId={pageId}
        mode={mode}
        onModeChange={setMode}
        onPageChange={setPageId}
        onFinish={() => setStage('explore')}
        onExit={() => setStage('intro')}
      />
    );
  }

  if (stage === 'explore') {
    return (
      <StoryActivities
        story={story}
        onExit={() => setStage('read')}
        onRevisitPage={(id) => {
          setPageId(id);
          setStage('read');
        }}
        onComplete={(method) => {
          setParticipation(method);
          setStage('choose');
        }}
      />
    );
  }

  if (stage === 'draw' && engine && template) {
    return (
      <>
        {restoreProblem || saveError ? (
          <div role="alert" className="bg-secondary-soft px-l py-m text-[15px] text-ink">
            {saveError ?? restoreProblem}
          </div>
        ) : null}
        <ColoringStudio
          template={template}
          engine={engine}
          saveState={saveState}
          onRequestSave={requestSave}
          onDone={finish}
          onBack={() => setStage('choose')}
          busy={busy}
          wide={wide}
          handedness="right"
        />
      </>
    );
  }

  /* ------------------------------ Chrome ------------------------------- */

  return (
    <div className="relative min-h-[100dvh] bg-paper">
      <div aria-hidden className="absolute inset-x-0 top-0 h-[420px]" style={{ background: 'var(--gradient-dawn)' }} />
      <SparkleField count={12} seed={11} className="h-[420px]" />

      <header className="relative z-10 u-shell flex items-center justify-between py-l">
        <button onClick={() => navigate('/')} className="flex items-center gap-s font-display text-[15px] font-semibold text-ink-soft">
          <IconBack aria-hidden /> Home
        </button>
        <Logo size="sm" />
        <span className="w-[72px]" />
      </header>

      <main className="relative z-10 u-shell pb-3xl">
        {stage === 'intro' ? (
          <IntroStage onStart={() => setStage('read')} storyTitle={story.title} />
        ) : null}

        {stage === 'choose' ? (
          <ChooseStage
            story={story}
            participation={participation}
            onPick={openTemplate}
            onBack={() => setStage('explore')}
          />
        ) : null}

        {stage === 'result' && resultUrl ? (
          <ResultStage
            resultUrl={resultUrl}
            livingUrl={livingUrl}
            templateId={templateId}
            showLiving={showLiving}
            onToggleLiving={() => setShowLiving((v) => !v)}
            onAnother={() => {
              setShowLiving(false);
              setResultUrl(null);
              setLivingUrl(null);
              setArtwork(null);
              setTemplateId(null);
              setStage('choose');
            }}
          />
        ) : null}
      </main>
    </div>
  );
}

/* -------------------------------------------------------------------- */

function IntroStage({ onStart, storyTitle }: { onStart(): void; storyTitle: string }) {
  return (
    <div className="mx-auto max-w-2xl text-center animate-slide-up">
      <Chip tone="magic" icon={<SparkleGlyph className="h-3.5 w-3.5" />}>
        The whole thing, free, right now
      </Chip>
      <h1 className="mt-l font-display text-[34px] font-semibold leading-tight text-ink sm:text-[46px]">
        Read <span className="u-gradient-text">{storyTitle}</span>, then make it yours
      </h1>
      <p className="mx-auto mt-l max-w-xl text-[18px] leading-relaxed text-ink-soft">
        Six pages, one thinking game, and a picture you colour yourself — and then watch move.
        Nothing to sign up for, and nothing leaves this device.
      </p>

      <div className="mt-2xl overflow-hidden rounded-blob border-2 border-line shadow-cut-lg">
        <Scene id="pip-cover" className="aspect-[3/2] w-full" title="Pip the fox and Sway the moth at a moonlit pond" />
      </div>

      <div className="mt-2xl flex flex-col items-center gap-m">
        <Button variant="magic" size="xl" onClick={onStart} iconRight={<IconArrow />}>
          Start the story
        </Button>
        <p className="text-[14px] text-ink-faint">About 5 minutes to read, then as long as you like to draw.</p>
      </div>
    </div>
  );
}

function ChooseStage({
  story,
  participation,
  onPick,
  onBack,
}: {
  story: typeof PIP_AND_THE_PAPER_MOON;
  participation: ParticipationMethod | null;
  onPick(id: string): void;
  onBack(): void;
}) {
  return (
    <div className="animate-slide-up">
      <SectionHeading
        align="center"
        eyebrow={participation === 'accommodation' ? 'Straight to making' : 'You explored the story'}
        title="Now pick a picture to make"
        description="Three pictures from the story. They are ready now — no waiting, no generating."
      />
      <div className="mt-2xl grid gap-l sm:grid-cols-3">
        {story.currentVersion.templates.map((tpl, i) => (
          <button
            key={tpl.id}
            onClick={() => onPick(tpl.id)}
            className={cn(
              'group overflow-hidden rounded-blob border-[3px] border-line bg-card text-left shadow-cut transition hover:-translate-y-1.5 hover:shadow-cut-lg animate-pop-in',
              [`u-stack-1`, `u-stack-2`, `u-stack-3`][i],
            )}
          >
            <div className="aspect-square w-full bg-white p-m">
              <TemplateThumb templateId={tpl.id} />
            </div>
            <div className="border-t-2 border-line p-l">
              <h3 className="font-display text-[18px] font-semibold text-ink">{tpl.title}</h3>
              <p className="mt-xs flex items-center gap-xs text-[14px] font-semibold text-magic-deep">
                {tpl.livingSceneId ? (
                  <>
                    <SparkleGlyph className="h-3.5 w-3.5" /> Can come to life
                  </>
                ) : (
                  <span className="text-ink-faint">Colour it in</span>
                )}
              </p>
            </div>
          </button>
        ))}
      </div>
      <div className="mt-xl text-center">
        <Button variant="ghost" size="md" onClick={onBack} icon={<IconBack />}>
          Back to the story
        </Button>
      </div>
    </div>
  );
}

/**
 * Renders a template's own line art as its thumbnail — no separate asset.
 * Resolves against every published template, not one story's list.
 */
export function TemplateThumb({ templateId }: { templateId: string }) {
  const tpl = TEMPLATES.find((t) => t.id === templateId);
  if (!tpl) return null;
  return (
    <svg viewBox={`0 0 ${tpl.width} ${tpl.height}`} className="h-full w-full" aria-hidden>
      <path
        d={tpl.outline}
        fill="none"
        stroke="#2C1C38"
        strokeWidth={Math.max(2, tpl.width / 200)}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {tpl.detail ? <path d={tpl.detail} fill="#2C1C38" /> : null}
    </svg>
  );
}

function ResultStage({
  resultUrl,
  livingUrl,
  templateId,
  showLiving,
  onToggleLiving,
  onAnother,
}: {
  resultUrl: string;
  livingUrl: string | null;
  templateId: string | null;
  showLiving: boolean;
  onToggleLiving(): void;
  onAnother(): void;
}) {
  const tpl = PIP_AND_THE_PAPER_MOON.currentVersion.templates.find((t) => t.id === templateId);
  const scene = tpl?.livingSceneId ? LIVING_SCENES[tpl.livingSceneId] : null;

  return (
    <div className="mx-auto max-w-3xl animate-slide-up">
      <div className="relative mb-xl text-center">
        <SparkleBurst />
        <h1 className="relative font-display text-[32px] font-semibold text-ink sm:text-[40px]">
          You made it.
        </h1>
        <p className="mt-s text-[17px] text-ink-soft">
          Saved on this device. It is yours — nobody else can see it.
        </p>
      </div>

      {showLiving && scene ? (
        <LivingScene
          scene={scene}
          artworkUrl={livingUrl ?? resultUrl}
          backdropSceneId={scene.backdropSceneId}
          title="pip-and-the-paper-moon"
          onClose={onToggleLiving}
        />
      ) : (
        <div className="overflow-hidden rounded-blob border-2 border-line bg-white p-xl shadow-cut-lg">
          <img src={resultUrl} alt="The picture you coloured" className="mx-auto max-h-[52vh] w-auto" />
        </div>
      )}

      <div className="mt-xl flex flex-wrap justify-center gap-m">
        {scene && !showLiving ? (
          <Button variant="magic" size="lg" onClick={onToggleLiving} icon={<SparkleGlyph />}>
            Bring my story to life
          </Button>
        ) : null}
        <Button variant="outline" size="lg" onClick={onAnother}>
          Make another picture
        </Button>
      </div>

      <Card className="mt-2xl bg-primary-soft/60">
        <h2 className="font-display text-[20px] font-semibold text-ink">Want to keep it?</h2>
        <p className="mt-s text-[16px] leading-relaxed text-ink-soft">
          Your picture is saved in this browser. A grown-up can set up a free account to keep it
          safely, add it to a personal edition of the story, and carry it to another device.
          We will offer to bring this drawing across when you do.
        </p>
        <div className="mt-l flex flex-wrap gap-m">
          <Button variant="primary" size="md" to="/app/setup">
            Set up a parent account
          </Button>
          <Button variant="ghost" size="md" to="/how-it-works">
            See how the rest works
          </Button>
        </div>
      </Card>
    </div>
  );
}
